if (!customElements.get('ax-mosaic')) {
  customElements.define(
      'ax-mosaic',
      class AxMosaic extends HTMLElement {
        // Bento patterns per column count: each slot is [colSpan, rowSpan].
        // The 8-slot cycle repeats for any number of items.
        static PATTERNS = {
          3: [[2,2],[1,1],[2,1],[1,2],[1,1],[2,2],[2,1],[1,2]],
          4: [[3,2],[1,2],[2,2],[2,1],[1,1],[3,2],[2,2],[1,2]],
          5: [[3,3],[2,2],[2,2],[2,3],[1,1],[3,2],[3,2],[2,2]],
          6: [[4,3],[2,2],[2,2],[2,3],[1,1],[4,2],[3,2],[2,2]],
          7: [[4,3],[3,2],[3,2],[2,3],[1,1],[4,2],[3,2],[3,2]],
          8: [[5,3],[3,2],[3,2],[2,3],[1,1],[5,2],[3,2],[3,2]],
        };

        constructor() {
          super();
          this.onItemEnter = this.onItemEnter.bind(this);
          this.onItemLeave = this.onItemLeave.bind(this);
          this.onItemClick = this.onItemClick.bind(this);
          this.onKeyDown   = this.onKeyDown.bind(this);
          this.onResize    = this.onResize.bind(this);
        }

        connectedCallback() {
          this.container = this.querySelector('.ax-mosaic');
          this.callbackName = this.dataset.callback;
          this.items = Array.from(this.querySelectorAll('.ax-mosaic__item'));

          this.items.forEach((item) => {
            item.addEventListener('mouseenter', this.onItemEnter);
            item.addEventListener('focusin',    this.onItemEnter);
            item.addEventListener('mouseleave', this.onItemLeave);
            item.addEventListener('focusout',   this.onItemLeave);
            item.addEventListener('click',      this.onItemClick);
          });

          document.addEventListener('keydown', this.onKeyDown);
          window.addEventListener('resize', this.onResize);

          // Wait one frame so computed styles (CSS vars, media queries) are settled.
          requestAnimationFrame(() => this.assignSizes());
        }

        disconnectedCallback() {
          this.items.forEach((item) => {
            item.removeEventListener('mouseenter', this.onItemEnter);
            item.removeEventListener('focusin',    this.onItemEnter);
            item.removeEventListener('mouseleave', this.onItemLeave);
            item.removeEventListener('focusout',   this.onItemLeave);
            item.removeEventListener('click',      this.onItemClick);
          });

          document.removeEventListener('keydown', this.onKeyDown);
          window.removeEventListener('resize', this.onResize);
          clearTimeout(this._resizeTimer);
        }

        assignSizes() {
          if ((this.dataset.layout || 'mosaic') === 'columns') {
            this.items.forEach((item) => {
              item.style.gridColumn = 'span 1';
              item.style.gridRow    = 'span 1';
            });
            return;
          }

          // mosaic: bento pattern scaled to the actual rendered column count
          const style = getComputedStyle(this.container);

          // Actual rendered column count (handles CSS media-query overrides).
          const cols = style.gridTemplateColumns.trim().split(/\s+/).length;

          // Cap row spans when rows are tall to avoid enormous cells.
          const rowHeight = parseInt(style.getPropertyValue('--ax-mosaic-row-height')) || 80;
          const maxRows = rowHeight >= 150 ? 2 : 3;

          // Pick the closest predefined pattern; for unknown column counts scale
          // the 5-column reference pattern proportionally.
          const ref = AxMosaic.PATTERNS[cols]
              || AxMosaic.PATTERNS[5].map(([c, r]) => [
                Math.max(1, Math.min(cols, Math.round(c * cols / 5))), r
              ]);

          this.items.forEach((item, i) => {
            const [colSpan, rowSpan] = ref[i % ref.length];
            item.style.gridColumn = `span ${Math.min(colSpan, cols)}`;
            item.style.gridRow    = `span ${Math.min(rowSpan, maxRows)}`;
          });
        }

        onResize() {
          clearTimeout(this._resizeTimer);
          this._resizeTimer = setTimeout(() => this.assignSizes(), 150);
        }

        onItemEnter(event) {
          const item = event.currentTarget;
          this.collapseAll(item);
          item.classList.add('ax-mosaic__item--expanded');
          this.invokeCallback(item);

          // Show "click here to interact" overlay if not already interacting
          if (!this._expandedItem && !item.querySelector('.ax-mosaic__interact')) {
            const overlay = document.createElement('div');
            overlay.className = 'ax-mosaic__interact';
            overlay.textContent = 'click here to interact';
            item.appendChild(overlay);
          }
        }

        onItemLeave(event) {
          const item = event.currentTarget;
          item.classList.remove('ax-mosaic__item--expanded');
          const overlay = item.querySelector('.ax-mosaic__interact');
          if (overlay) overlay.remove();
        }

        onItemClick(event) {
          const item = event.currentTarget;
          if (this._expandedItem) return;

          const img = item.querySelector('img');
          if (!img) return;

          const svgUrl = img.getAttribute("data-annotatex-svg-url")
          if (!svgUrl) return;

          // Remove the overlay before expanding
          const overlay = item.querySelector('.ax-mosaic__interact');
          if (overlay) overlay.remove();

          this._expandedItem = item;

          // hide footer because its in front of here
          const footer = document.querySelector(".footer");
          if (footer) footer.style.display = 'none';

          // Create expanded div as the first child of the grid row
          const expandedDiv = document.createElement('div');
          expandedDiv.className = 'ax-mosaic__expanded';
          this._expandedDiv = expandedDiv;

          // Header bar: the image title (its alt) on the left and a close button
          // on the right. Clicks on the bar are stopped so they don't reach the
          // backdrop close handler; only the close button calls reset().
          const header = document.createElement('div');
          header.className = 'ax-mosaic__expanded-header';
          header.addEventListener('click', (event) => event.stopPropagation());

          const title = document.createElement('h2');
          title.className = 'ax-mosaic__expanded-title';
          title.textContent = img.getAttribute('alt') || '';

          const closeBtn = document.createElement('button');
          closeBtn.type = 'button';
          closeBtn.className = 'ax-mosaic__expanded-close';
          closeBtn.setAttribute('aria-label', 'Close');
          closeBtn.innerHTML = '&times;';
          closeBtn.addEventListener('click', (event) => {
            event.stopPropagation();
            this.reset();
          });

          header.appendChild(title);
          header.appendChild(closeBtn);
          expandedDiv.appendChild(header);

          document.body.appendChild(expandedDiv);

          const object = document.createElement("object")
          object.setAttribute("data", svgUrl)
          object.setAttribute("type", "image/svg+xml")
          // Fill the wrapper. NOTE: do not use height:auto here — Chrome renders
          // an <object> SVG with height:auto at a tiny size, and an inline style
          // would override annotateX's autoResize fit rule (which needs 100%).
          object.setAttribute("style", "width:100%;height:100%")

          // Move the OBJECT tag into the expanded div
          expandedDiv.appendChild(object);

          // Close when clicking the background area outside the object
          expandedDiv.addEventListener('click', () => this.reset());

          // Pin the annotateX API origin to the SVG's host so relative crop-image
          // URLs (annotation thumbnails) resolve against the annotate-x backend
          // instead of this storefront's origin.
          try { annotateX.setOrigin(new URL(svgUrl, location.href).origin); } catch (e) {}

          annotateX.build(object, {autoResize:true, legendTitle: false, controls: false})
        }

        reset() {
          // The expanded div is appended to <body>, so use the stored reference
          // (fall back to a document lookup for safety).
          const expandedDiv = this._expandedDiv || document.querySelector('.ax-mosaic__expanded');
          if (!expandedDiv) return;

          expandedDiv.remove();
          this._expandedDiv = null;
          this._expandedItem = null;

          // restore footer visibility
          const footer = document.querySelector(".footer");
          if (footer) footer.style.display = '';
        }

        onKeyDown(event) {
          if (event.key === 'Escape') {
            this.collapseAll();
            this.reset();
          }
        }

        collapseAll(except) {
          this.items.forEach((item) => {
            if (item !== except) item.classList.remove('ax-mosaic__item--expanded');
          });
        }

        invokeCallback(item) {
          const detail = {
            item,
            url: item.dataset.url || '',
            description: item.dataset.description || '',
            object: item.querySelector('object'),
          };

          const eventDetail = { ...detail, mosaic: this };
          this.dispatchEvent(new CustomEvent('ax-mosaic:hover', { bubbles: true, detail: eventDetail }));
          window.dispatchEvent(new CustomEvent('ax-mosaic:hover', { detail: eventDetail }));

          const fn = this.callbackName && window[this.callbackName];
          if (typeof fn === 'function') {
            try { fn(detail); }
            catch (error) {
              console.error('[ax-mosaic] callback "' + this.callbackName + '" threw:', error);
            }
          }
        }
      }
  );
}
